import { useRef, useState } from 'react';
import { useBiz } from '../BizContext';
import { Avatar } from '../bits';

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const TAIL = /(^|\s)@([^\s@]{0,24}(?: [^\s@]{0,24})?)$/;

// Which admins are @mentioned in a piece of text (by full name, case-insensitive).
export const mentionIds = (text, owners) => owners.filter((o) => o.name && new RegExp(`@${esc(o.name)}(?![\\w])`, 'i').test(text || '')).map((o) => o.id);

// Text input / textarea with an @name picker over the admin list.
export function MentionInput({ value, onChange, multiline = false, onKeyDown, testId, ...rest }) {
  const { owners } = useBiz();
  const ref = useRef(null);
  const [q, setQ] = useState(null);
  const [idx, setIdx] = useState(0);
  const matches = q == null ? [] : owners.filter((o) => o.name && o.name.toLowerCase().startsWith(q.toLowerCase())).slice(0, 6);
  const update = (val, caret) => { onChange(val); const m = TAIL.exec(val.slice(0, caret)); setQ(m ? m[2] : null); setIdx(0); };
  const pick = (o) => {
    const el = ref.current; const caret = el?.selectionStart ?? value.length;
    const before = value.slice(0, caret).replace(/@[^\s@]{0,24}(?: [^\s@]{0,24})?$/, `@${o.name} `);
    onChange(before + value.slice(caret)); setQ(null);
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(before.length, before.length); });
  };
  const keys = (e) => {
    if (matches.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => (i + 1) % matches.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => (i - 1 + matches.length) % matches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(matches[idx]); return; }
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); setQ(null); return; }
    }
    onKeyDown?.(e);
  };
  const Field = multiline ? 'textarea' : 'input';
  return (
    <div className="relative flex-1 min-w-0">
      <Field ref={ref} value={value} onChange={(e) => update(e.target.value, e.target.selectionStart)} onKeyDown={keys} onBlur={() => setTimeout(() => setQ(null), 150)} data-testid={testId} {...rest} />
      {matches.length > 0 && (
        <ul className="absolute left-0 top-full mt-1 z-20 biz-card p-1 min-w-[200px] shadow-lg" style={{ background: 'var(--paper-2)' }} data-testid={`${testId}-mentions`}>
          {matches.map((o, i) => (
            <li key={o.id}><button type="button" onMouseDown={(e) => { e.preventDefault(); pick(o); }} className="w-full flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded-lg" style={{ background: i === idx ? 'var(--paper-3)' : 'transparent' }} data-testid={`${testId}-mention-${o.id}`}><Avatar ownerId={o.id} name={o.name} size={20} /> {o.name}</button></li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Renders text with @Name highlighted for known admins.
export function RichText({ text, className }) {
  const { owners } = useBiz();
  const names = owners.map((o) => o.name).filter(Boolean).sort((a, b) => b.length - a.length);
  if (!text || !names.length) return <p className={className}>{text}</p>;
  const re = new RegExp(`(@(?:${names.map(esc).join('|')}))(?![\\w])`, 'gi');
  const parts = text.split(re);
  return (
    <p className={className}>
      {parts.map((p, i) => (p.startsWith('@') && names.some((n) => n.toLowerCase() === p.slice(1).toLowerCase())
        ? <span key={i} className="font-semibold rounded px-1 -mx-0.5" style={{ background: 'var(--sage-soft)', color: 'var(--sage-deep)' }} data-testid="ws-mention">{p}</span>
        : <span key={i}>{p}</span>))}
    </p>
  );
}
